import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../config/api';
import ConfirmModal from '../shared/ConfirmModal';

const WordListCard = ({ list, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedList, setEditedList] = useState({
    name: list.name,
    description: list.description || ''
  });
  const [error, setError] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const updatedList = await api.updateWordList(list.id, editedList);
      onUpdate(updatedList);
      setIsEditing(false);
      setError('');
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas aktualizacji listy');
    }
  };

  const handleCancel = () => {
    setEditedList({
      name: list.name,
      description: list.description || ''
    });
    setIsEditing(false);
    setError('');
  };

  const handleDelete = async () => {
    try {
      await api.deleteWordList(list.id);
      onDelete(list.id);
    } catch (err) {
      setError(err.message || 'Wystąpił błąd podczas usuwania listy');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 hover:shadow-md transition-shadow p-4 relative group">
      <div className="absolute top-2 right-2 flex items-center space-x-1">
        {isEditing ? (
          <>
            <button
              onClick={handleSave}
              className="p-1 text-neutral-400 hover:text-green-500 rounded-full hover:bg-neutral-100 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </button>
            <button
              onClick={handleCancel}
              className="p-1 text-neutral-400 hover:text-neutral-600 rounded-full hover:bg-neutral-100 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleEdit}
              className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-blue-500 rounded-full hover:bg-neutral-100 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-red-500 rounded-full hover:bg-neutral-100 transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </>
        )}
      </div>

      {isEditing ? (
        <div className="block">
          <div className="flex flex-col space-y-3">
            <div className="flex flex-col">
              <div className="flex-1">
                <p className="text-xs text-neutral-500 mb-1">Nazwa listy</p>
                <input
                  type="text"
                  value={editedList.name}
                  onChange={(e) => setEditedList({ ...editedList, name: e.target.value })}
                  className="w-full p-1 text-sm font-medium text-neutral-800 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <p className="text-xs text-neutral-500 mb-1">Opis</p>
              <textarea
                value={editedList.description}
                onChange={(e) => setEditedList({ ...editedList, description: e.target.value })}
                className="w-full p-1 text-sm font-medium text-neutral-800 border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                rows="2"
              />
            </div>
          </div>
        </div>
      ) : (
        <Link to={`/lists/${list.id}`} className="block">
          <div className="flex flex-col space-y-3">
            <div className="flex flex-col">
              <div className="flex-1">
                <p className="text-xs text-neutral-500 mb-1">Nazwa listy</p>
                <p className="text-sm font-medium text-neutral-800">{list.name}</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-xs text-neutral-500 mb-1">Opis</p>
              <p className="text-sm font-medium text-neutral-800 mb-2">{list.description || 'Brak opisu'}</p>
            </div>
          </div>
        </Link>
      )}

      <div className={`absolute bottom-2 right-2 text-xs text-neutral-400`}>
        {list.language_name} | słówek: {list.words_count || 0}
      </div>

      {error && (
        <div className="mt-2 p-2 text-xs text-red-600 bg-red-50 rounded">
          {error}
        </div>
      )}

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Usuń listę"
        message="Czy na pewno chcesz usunąć tę listę? Wszystkie słówka zostaną usunięte."
      />
    </div>
  );
};

export default WordListCard; 